/**
 * Shop funnel helpers on top of track / trackEcommerce: one shape for every
 * web product (course, package, web app year, SUP tokens, specialist month,
 * consultation, gift), so GA4 reports and the Ads value read the same way.
 *
 *   view_item_list → select_item / view_item → add_to_cart → begin_checkout
 *   → checkout_redirect (→ Stripe) → purchase | checkout_cancel
 *
 * checkout_redirect also leaves a per-tab note (sessionStorage) of what went
 * to Stripe. When the tab comes back:
 *  - with a success marker (payment_success, session_id, tokens_paid,
 *    paid=1, status=success) the note waits for the return page, which
 *    sends `purchase` with the same value and items (trackPurchase);
 *  - without one, it was a cancel: `checkout_cancel` (detectCheckoutReturn,
 *    called by AnalyticsRoot on load and on a Back-restored page).
 *
 * transaction_id = the Stripe Checkout Session id; site_server sends the
 * same id through Measurement Protocol and GA4 dedups the pair. Ids that
 * are not real sales (comp_ 100 %-off, grant_) are never sent; Stripe test
 * sessions (cs_test_) only from internal traffic, so QA shows in DebugView.
 *
 * item_category is the server's (site_server services_fastapi/ga4_mp.py):
 * course | package | app_subscription_web | sup_tokens | specialist_month |
 * consultation. Donations and the $3 avatar are not sales: `donate` only, and
 * only from the server (trackDonate).
 */
import { cleanText } from "@/features/activityLog/lib/sanitize";
import { track, trackEcommerce, type EcommerceItem, type EventParams } from "./dataLayer";
import { isInternalTraffic } from "./internal";

export type ProductCategory =
    | "course"
    | "package"
    | "app_subscription_web"
    | "sup_tokens"
    | "specialist_month"
    | "consultation";

/** Every checkout kind the funnel events carry (`kind` param). */
export type CheckoutKind = ProductCategory | "gift" | "donation" | "avatar";

export const CURRENCY = "USD";

/** Cents → dollars, rounded to the cent. */
export const usd = (cents: number | null | undefined): number =>
    typeof cents === "number" && Number.isFinite(cents) ? Math.round(cents) / 100 : 0;

/** One GA4 item, quantity 1, price in dollars. */
export const productItem = (
    id: string,
    name: string | null | undefined,
    category: ProductCategory,
    price: number,
    extra?: Record<string, string | number | boolean | undefined>,
): EcommerceItem => ({
    item_id: id,
    item_name: name || id,
    item_category: category,
    price: Number.isFinite(price) ? Math.round(price * 100) / 100 : 0,
    quantity: 1,
    ...(extra || {}),
});

const sumItems = (items: EcommerceItem[]): number =>
    Math.round(items.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0) * 100) / 100;

export interface CheckoutInfo {
    kind: CheckoutKind;
    items: EcommerceItem[];
    /** What the buyer is shown to pay; defaults to the sum of the items. */
    value?: number;
    /**
     * The value is only the list price: a coupon or a friend's link may lower
     * it and only the server knows by how much. The return page then sends
     * `purchase` only with the amount Stripe charged (trackPurchase paidValue).
     */
    approxValue?: boolean;
    coupon?: string | null;
    /** Flat params next to `ecommerce` (cta_location, list_id, …). */
    extra?: EventParams;
}

const ecommerceOf = (c: CheckoutInfo) => ({
    currency: CURRENCY,
    value: typeof c.value === "number" && Number.isFinite(c.value) ? c.value : sumItems(c.items),
    items: c.items,
    ...(c.coupon ? { coupon: c.coupon } : {}),
});

/** A list of products on screen (packages grid, course list, pack picker). */
export const trackViewItemList = (listId: string, items: EcommerceItem[], extra?: EventParams): boolean =>
    items.length > 0 &&
    trackEcommerce(
        "view_item_list",
        { currency: CURRENCY, value: sumItems(items), items: items.map((i) => ({ ...i, item_list_id: listId })) },
        { item_list_id: listId, ...extra },
    );

/** A product opened from a list or a CTA. */
export const trackSelectItem = (listId: string, item: EcommerceItem, extra?: EventParams): boolean =>
    trackEcommerce(
        "select_item",
        { currency: CURRENCY, value: item.price, items: [{ ...item, item_list_id: listId }] },
        { item_list_id: listId, ...extra },
    );

/** One product's page or offer seen. */
export const trackViewItem = (item: EcommerceItem, extra?: EventParams): boolean =>
    trackEcommerce("view_item", { currency: CURRENCY, value: item.price, items: [item] }, extra);

/** A pack / plan chosen (before sign-in or payment). */
export const trackAddToCart = (c: CheckoutInfo): boolean =>
    trackEcommerce("add_to_cart", ecommerceOf(c), { kind: c.kind, ...c.extra });

/** The pay button pressed and the request to create the Stripe session sent. */
export const trackBeginCheckout = (c: CheckoutInfo): boolean =>
    trackEcommerce("begin_checkout", ecommerceOf(c), { kind: c.kind, ...c.extra });

// ── Per-tab note of the checkout that went to Stripe ─────────────────────

const PENDING_KEY = "analytics_checkout";
const RETURNED_KEY = "analytics_checkout_returned";
/** Longer than a Stripe session lives on screen; older notes are stale. */
export const CHECKOUT_NOTE_TTL_MS = 3 * 60 * 60 * 1000;

export interface CheckoutNote {
    kind: CheckoutKind;
    items: EcommerceItem[];
    value: number;
    coupon?: string;
    /** CheckoutInfo.approxValue. */
    approx?: boolean;
    ts: number;
}

const readNote = (key: string, now: number): CheckoutNote | null => {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const n = JSON.parse(raw) as CheckoutNote | null;
        if (!n || typeof n.ts !== "number" || !Array.isArray(n.items) || now - n.ts > CHECKOUT_NOTE_TTL_MS) {
            sessionStorage.removeItem(key);
            return null;
        }
        return n;
    } catch {
        return null;
    }
};

const dropNote = (key: string): void => {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // nothing stored
    }
};

/**
 * The Stripe session was created and the browser is about to leave for it.
 * Pushes `checkout_redirect` and leaves the note for the return.
 */
export const trackCheckoutRedirect = (c: CheckoutInfo): boolean => {
    const e = ecommerceOf(c);
    const note: CheckoutNote = {
        kind: c.kind,
        items: c.items,
        value: e.value,
        ...(c.coupon ? { coupon: c.coupon } : {}),
        ...(c.approxValue ? { approx: true } : {}),
        ts: Date.now(),
    };
    try {
        sessionStorage.setItem(PENDING_KEY, JSON.stringify(note));
        sessionStorage.removeItem(RETURNED_KEY);
    } catch {
        // no note — no client purchase / cancel; the server still reports
    }
    return track("checkout_redirect", {
        kind: c.kind,
        item_id: c.items[0]?.item_id,
        value: e.value,
        currency: CURRENCY,
        coupon: c.coupon || undefined,
        ...c.extra,
    });
};

/** The checkout could not be created (HTTP error, no url, validation). */
export const trackCheckoutError = (kind: CheckoutKind, reason: unknown, extra?: EventParams): boolean =>
    track("checkout_error", { kind, reason: errorReason(reason), ...extra });

/** The buyer pressed pay but the page stopped them (terms, email, sign-in…). */
export const trackCheckoutBlocked = (kind: CheckoutKind, reason: string, extra?: EventParams): boolean =>
    track("checkout_blocked", { kind, reason, ...extra });

/** A coupon checked: result = "ok" or the server's reason. */
export const trackCouponApply = (kind: CheckoutKind, result: string, extra?: EventParams): boolean =>
    track("coupon_apply", { kind, result, ...extra });

/**
 * Short reason: an Error's message or a string, cleaned like every other free
 * text — emails and digit runs (phone, order, card numbers) masked, ≤ 100.
 */
export const errorReason = (reason: unknown): string => {
    const raw = reason instanceof Error ? reason.message : typeof reason === "string" ? reason : "";
    return cleanText(raw || "unknown", 100) || "unknown";
};

const SUCCESS_MARKERS: Array<[string, (v: string) => boolean]> = [
    ["payment_success", (v) => v === "true" || v === "1"],
    ["session_id", (v) => !!v],
    ["tokens_paid", (v) => !!v],
    ["paid", (v) => v === "1"],
    ["status", (v) => v === "success"],
];

/**
 * On a fresh load (or a page Back restored from the cache): was the tab at
 * Stripe just now? A success return keeps the note for the return page; any
 * other return is `checkout_cancel`.
 */
export const detectCheckoutReturn = (search?: string, now: number = Date.now()): void => {
    const note = readNote(PENDING_KEY, now);
    if (!note) return;
    let params: URLSearchParams;
    try {
        params = new URLSearchParams(search ?? window.location.search);
    } catch {
        return;
    }
    const success = SUCCESS_MARKERS.some(([k, ok]) => {
        const v = params.get(k);
        return v !== null && ok(v);
    });
    dropNote(PENDING_KEY);
    if (success) {
        try {
            sessionStorage.setItem(RETURNED_KEY, JSON.stringify(note));
        } catch {
            // the return page falls back to its own data
        }
        return;
    }
    track("checkout_cancel", {
        kind: note.kind,
        item_id: note.items[0]?.item_id,
        value: note.value,
        currency: CURRENCY,
        seconds_at_checkout: Math.max(0, Math.round((now - note.ts) / 1000)),
    });
};

// The address the document was loaded with: return pages strip their markers
// (payment_success, tokens_paid…) in effects that may run before ours.
const BOOT_SEARCH = (() => {
    try {
        return window.location.search;
    } catch {
        return "";
    }
})();

let returnWatchInstalled = false;

/**
 * detectCheckoutReturn for the address this document was loaded with, and
 * again whenever the browser restores the page from bfcache (Back from Stripe).
 */
export const installCheckoutReturnWatch = (): void => {
    if (returnWatchInstalled || typeof window === "undefined") return;
    returnWatchInstalled = true;
    detectCheckoutReturn(BOOT_SEARCH);
    window.addEventListener("pageshow", (e) => {
        if ((e as PageTransitionEvent).persisted) detectCheckoutReturn();
    });
};

/**
 * The note of the checkout this tab returned from (or is still marked as
 * pending, when the return page runs before AnalyticsRoot), if its kind is
 * one of `kinds`. Taken: a second call gets null.
 */
export const takeCheckoutNote = (kinds?: CheckoutKind[], now: number = Date.now()): CheckoutNote | null => {
    for (const key of [RETURNED_KEY, PENDING_KEY]) {
        const n = readNote(key, now);
        if (n && (!kinds || kinds.includes(n.kind))) {
            dropNote(key);
            return n;
        }
    }
    return null;
};

/** A transaction id we may send: a real Stripe session (test ones only from internal traffic). */
export const isReportableTransaction = (txId: string | null | undefined): txId is string => {
    if (!txId || !/^[A-Za-z0-9_]{6,200}$/.test(txId)) return false;
    if (/^(comp|grant)_/.test(txId)) return false;
    if (/^cs_test_/.test(txId)) return isInternalTraffic();
    return true;
};

export interface PurchaseInput {
    kind: CheckoutKind;
    items: EcommerceItem[];
    value?: number;
    coupon?: string | null;
}

// Stripe's minor units (https://docs.stripe.com/currencies), as site_server's
// ga4_mp._decimals.
const ZERO_DECIMAL = new Set(["bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga", "pyg", "rwf", "ugx", "vnd", "vuv", "xaf", "xof", "xpf"]);
const THREE_DECIMAL = new Set(["bhd", "jod", "kwd", "omr", "tnd"]);

const roundIn = (v: number, currency: string): number => {
    const c = currency.toLowerCase();
    const d = ZERO_DECIMAL.has(c) ? 0 : THREE_DECIMAL.has(c) ? 3 : 2;
    return Math.round(v * 10 ** d) / 10 ** d;
};

/**
 * What Stripe charged: a session's amount_total + currency (minor units) →
 * { value, currency } in major units. With Adaptive Pricing that is the
 * buyer's local amount (₹1 320.00), the same the server reports.
 */
export const stripeCharged = (amount: unknown, currency: unknown): { value: number; currency: string } | null => {
    const minor = Number(amount);
    const cur = String(currency || "").trim().toLowerCase();
    if (!/^[a-z]{3}$/.test(cur) || !Number.isFinite(minor) || minor <= 0) return null;
    const d = ZERO_DECIMAL.has(cur) ? 0 : THREE_DECIMAL.has(cur) ? 3 : 2;
    return { value: roundIn(Math.round(minor) / 10 ** d, cur), currency: cur.toUpperCase() };
};

/**
 * The web `purchase` on a success return. `kinds` picks the tab's checkout
 * note (its items and coupon; its value too, unless `paidValue` — the amount
 * Stripe charged, in `paidCurrency` (default USD; the buyer's local currency
 * under Adaptive Pricing, as the server sends it — GA4 keeps the first copy
 * of a transaction_id, so both must agree) — is known, which always wins); `fallback` is used
 * without a note. A note whose value was only the list price (approx: a
 * coupon or a friend's link in play) needs `paidValue`, else nothing is sent.
 * Nothing sent → the server's Measurement Protocol purchase covers it, with
 * the charged amount. Once per transaction id.
 */
export const trackPurchase = (
    transactionId: string | null | undefined,
    opts: {
        kinds: CheckoutKind[];
        fallback?: PurchaseInput | null;
        paidValue?: number | null;
        paidCurrency?: string | null;
        extra?: EventParams;
    },
): boolean => {
    if (!isReportableTransaction(transactionId)) return false;
    const note = takeCheckoutNote(opts.kinds);
    const currency = /^[A-Za-z]{3}$/.test(opts.paidCurrency || "") ? String(opts.paidCurrency).toUpperCase() : CURRENCY;
    const paid =
        typeof opts.paidValue === "number" && Number.isFinite(opts.paidValue) && opts.paidValue > 0
            ? roundIn(opts.paidValue, currency)
            : null;
    // A local-currency charge with no amount to show: the note's USD would be
    // the first copy GA4 keeps — the server's copy alone is right.
    if (paid === null && currency !== CURRENCY) return false;
    if (note?.approx && paid === null) return false;
    let data: PurchaseInput | null = note
        ? { kind: note.kind, items: note.items, value: note.value, coupon: note.coupon }
        : opts.fallback ?? null;
    if (data && paid !== null) {
        // One item (every web checkout): its price is what was paid, so item
        // revenue and the purchase value agree.
        const items = data.items.length === 1 ? [{ ...data.items[0], price: paid }] : data.items;
        data = { ...data, items, value: paid };
    }
    if (!data || data.items.length === 0) return false;
    if (data.kind === "donation" || data.kind === "avatar") return false;
    return trackEcommerce(
        "purchase",
        { ...ecommerceOf(data), ...(paid !== null ? { currency } : {}), transaction_id: transactionId },
        { kind: data.kind, affiliation: "web", ...opts.extra },
    );
};

/**
 * A paid donation or $3 avatar payment came back: the tab's checkout note is
 * dropped and NOTHING is pushed. site_server's webhook sends `donate` through
 * Measurement Protocol for every paid one (owner 07.10: donate, never
 * purchase) — GA4 dedups only `purchase` by transaction_id, so a web copy
 * would count every donation twice. Always false.
 */
export const trackDonate = (
    _transactionId: string | null | undefined,
    opts: { kind: "donation" | "avatar"; value?: number; extra?: EventParams },
): boolean => {
    takeCheckoutNote([opts.kind]);
    return false;
};
